import Vue from 'vue';
import pendoService from '@/modules/externalScripts/services/pendoService';
import store from '@/store';

const isAnalyticsDisabled = () => store.getters['BusinessStore/isAnalyticsDisabled'];
const isImpersonating = () => store.getters['AuthStore/isImpersonating'];

let isUserSet = false;
let eventQueue = [];

const logMixpanelEvent = (action, ...args) => {
  if (window.localStorage.getItem('log-all-mixpanle')) {
    console.log(`[Mixpanel] ${action}`, ...args);
  }
};

const trackMixpanelEvent = (eventName, options = {}) => {
  if (isAnalyticsDisabled() || !window.mixpanel) {
    return;
  }

  if (!isUserSet) {
    eventQueue.push({ eventName, options });
    return;
  }

  logMixpanelEvent(eventName, options);
  window.mixpanel.track(eventName, {
    ...options,
    impersonation: isImpersonating(),
  });
};

const flushEventQueue = () => {
  if (!isUserSet) {
    return;
  }

  if (isAnalyticsDisabled() || !window.mixpanel) {
    eventQueue = [];
    return;
  }

  eventQueue.forEach(({ eventName, options }) => {
    trackMixpanelEvent(eventName, options);
  });

  eventQueue = [];
};

const AnalyticsPlugin = {
  // eslint-disable-next-line no-unused-vars
  install(app, vueOptions) {
    // eslint-disable-next-line no-param-reassign
    app.prototype.$track = function track(eventName, options = {}) {
      if (isAnalyticsDisabled()) {
        return;
      }
      this.$trackMixpanel(eventName, options);
      this.$trackGA(eventName, options);
      this.$trackPendo(eventName, options);
    };

    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackMixpanel = trackMixpanelEvent;

    /*
     * INTERNAL: $trackGA - Only sends to Google Analytics (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackGA = function trackGA(eventName, options = {}) {
      if (!isAnalyticsDisabled() && window.ga) {
        window.ga('send', {
          hitType: 'event',
          eventCategory: options.category || 'User Action',
          eventAction: eventName,
          eventLabel: options.label || '',
        });
      }
    };

    /*
     * INTERNAL: $trackPendo - Only sends to Pendo (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackPendo = function trackPendoInternal(eventName, options = {}) {
      if (!isAnalyticsDisabled() && window.pendo?.track && !pendoService.isPendoDenied()) {
        window.pendo.track(eventName, options);
      }
    };

    /**
     * Set username/user ID for analytics
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$setUsername = function setUsername(username) {
      if (isAnalyticsDisabled()) return;

      if (username) {
        if (window.mixpanel && typeof window.mixpanel.identify === 'function') {
          isUserSet = true;
          logMixpanelEvent('identify', username);
          window.mixpanel.identify(username);
        }

        if (window.ga && typeof window.ga === 'function') {
          window.ga('set', 'userId', username);
        }

        flushEventQueue();
      } else {
        isUserSet = false;
      }
    };

    // eslint-disable-next-line no-param-reassign
    app.prototype.$setUserProperties = function setUserProperties(properties = {}) {
      if (isAnalyticsDisabled()) return;
      if (window.mixpanel?.people) {
        logMixpanelEvent('people.set', properties);
        window.mixpanel.people.set(properties);
      }

      // (GA doesn't have a direct equivalent to Mixpanel's people properties)
    };

    /**
     * Register super properties - attached to all future events
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$registerSuperProperties = function registerSuperProperties(properties = {}) {
      if (isAnalyticsDisabled()) return;
      if (window.mixpanel) {
        logMixpanelEvent('register', properties);
        window.mixpanel.register(properties);
      }
    };

    /**
     * Register user with Mixpanel after login
     * Sets identity and user properties (partner_name, staff_uid, business_uid)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$registerMixpanelUser = function registerMixpanelUser() {
      if (isAnalyticsDisabled()) return;

      const staffUid = store.getters['AuthStore/staffUid'] || '';
      const businessUid = store.getters['AuthStore/businessUid'] || '';
      const partnerName = store.getters['BusinessStore/brandName'] || '';

      this.$setUsername(staffUid);
      this.$setUserProperties({
        partner_name: partnerName,
        staff_uid: staffUid,
        business_uid: businessUid,
      });
      this.$registerSuperProperties({
        partner_name: partnerName,
        staff_uid: staffUid,
        business_uid: businessUid,
      });
    };

    /**
     * Track page view with English page name
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackPageName = function trackPageName(pageNameEn) {
      if (isAnalyticsDisabled() || !pageNameEn) return;

      this.$registerSuperProperties({ page_name: pageNameEn });
      this.$trackMixpanel('Page View', { page_name: pageNameEn });
    };

    window.addEventListener('message', (event) => {
      // IMPORTANT: Mirrors behavior from vue/src/plugins/analytics.js
      // If POV is hosting a micro-service iframe - get analytics events
      // Consider any modifications made here to be included in the Vue equivalent
      const eventData = event.data;

      // Handle analytics events from iframes (Angular/Vuetage)
      if (eventData.event === 'analyticsServiceEvent') {
        const data = eventData.data;

        // Handle different types of analytics calls
        switch (data.method) {
          case 'setUserProperties':
            app.prototype.$setUserProperties(data.properties);
            break;

          case 'setUsername':
            app.prototype.$setUsername(data.username);
            break;

          default:
            if (data.eventName) {
              if (eventData.origin) data.options.source = eventData.origin;
              app.prototype.$track(data.eventName, data.options);
            }
            break;
        }
      }
    });
  },
};

Vue.use(AnalyticsPlugin);

export default AnalyticsPlugin;
